// Reusable, accessible confirm + prompt modals to replace window.confirm/prompt
// (which Mac/Chrome silently blocks after multiple dialogs in a session).
import { useEffect, useState } from "react";
import { X } from "@phosphor-icons/react";

export function ConfirmModal({ open, title, message, confirmLabel = "Confirm", cancelLabel = "Cancel", danger = false, onConfirm, onClose }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="confirm-modal">
      <div className="absolute inset-0 bg-[#1C1B1A]/40 backdrop-blur-sm" onClick={onClose} />
      <div className="modal-safe relative z-10 w-full max-w-md bg-white border hairline rounded-xl shadow-2xl overflow-hidden">
        <div className="px-6 py-5">
          <div className="font-serif-display text-xl text-[#1C1B1A]">{title}</div>
          {message && <p className="text-sm text-[#6B6A68] mt-2 leading-relaxed whitespace-pre-line">{message}</p>}
        </div>
        <div className="px-6 py-3 bg-[#FDFCF8] border-t hairline flex justify-end gap-2">
          <button onClick={onClose} data-testid="confirm-cancel" className="px-4 py-2 rounded-md text-[#6B6A68] hover:bg-[#F0F1EE] text-sm">
            {cancelLabel}
          </button>
          <button onClick={() => { onConfirm?.(); onClose?.(); }} data-testid="confirm-ok"
            className={`px-5 py-2 rounded-md text-white text-sm ${danger ? "bg-[#C16E5A] hover:bg-[#A65A48]" : "bg-[#1C1B1A] hover:bg-[#3A4A3E]"}`}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export function PromptModal({ open, title, label, defaultValue = "", placeholder = "", submitLabel = "Save", onSubmit, onClose, children }) {
  const [value, setValue] = useState(defaultValue);
  // Reset value whenever the modal re-opens so each invocation gets the fresh defaultValue.
  useEffect(() => {
    if (open) setValue(defaultValue);
  }, [open, defaultValue]);
  if (!open) return null;
  const submit = (e) => {
    e?.preventDefault();
    if (!value.trim()) return;
    onSubmit?.(value.trim());
    onClose?.();
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="prompt-modal">
      <div className="absolute inset-0 bg-[#1C1B1A]/40 backdrop-blur-sm" onClick={onClose} />
      <form onSubmit={submit} className="modal-safe relative z-10 w-full max-w-md bg-white border hairline rounded-xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b hairline">
          <div className="font-serif-display text-lg leading-none text-[#1C1B1A]">{title}</div>
          <button type="button" onClick={onClose} className="p-2 rounded-md text-[#6B6A68] hover:bg-[#F0F1EE]"><X size={18} /></button>
        </div>
        <div className="px-6 py-5">
          {label && <label className="block text-sm text-[#6B6A68] mb-1.5">{label}</label>}
          <input
            data-testid="prompt-input"
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={placeholder}
            className="w-full bg-white border hairline rounded-md px-4 py-3 focus:outline-none focus:ring-1 focus:ring-[#4A5D4E] focus:border-[#4A5D4E]"
          />
          {children}
        </div>
        <div className="px-6 py-3 bg-[#FDFCF8] border-t hairline flex justify-end gap-2">
          <button type="button" onClick={onClose} data-testid="prompt-cancel" className="px-4 py-2 rounded-md text-[#6B6A68] hover:bg-[#F0F1EE] text-sm">Cancel</button>
          <button type="submit" data-testid="prompt-ok" className="px-5 py-2 rounded-md bg-[#1C1B1A] text-white hover:bg-[#3A4A3E] text-sm disabled:opacity-50" disabled={!value.trim()}>
            {submitLabel}
          </button>
        </div>
      </form>
    </div>
  );
}
